// 생산오더 수신/조회
import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Card } from '../components/Card'
import { Button } from '../components/Button'
import { SkeletonRows } from '../components/Skeleton'
import { api } from '../lib/api'
import { useToast } from '../components/Toast'
import { useGlobalReveal } from '../hooks/useReveal'
import { useAuth, hasPerm } from '../lib/auth'
import { useT } from '../lib/i18n'

interface PO {
  production_order_id: string
  item_code: string
  quantity: number
  due_date: string
  priority: number
  status: 'received' | 'planned' | 'released' | 'closed'
}

export function ProductionOrdersPage() {
  useGlobalReveal()
  const toast = useToast()
  const { t } = useT()
  const { user } = useAuth()
  const canWrite = hasPerm(user, 'production_order', 'write')

  const [list, setList] = useState<PO[] | null>(null)
  const [busy, setBusy] = useState(false)

  const reload = async () => {
    try {
      const data = await api.get<PO[]>('/production-orders?limit=50')
      setList(data)
    } catch (e: any) {
      toast.push('error', e?.message ?? t('toast.fetchFailed'))
    }
  }
  useEffect(() => { reload() }, [])

  const receive = async () => {
    setBusy(true)
    try {
      const sample = {
        orders: [
          {
            external_id: `ERP-${Date.now()}-A`,
            item_code: 'ITEM-A100',
            quantity: 250,
            due_date: '2026-06-30',
            priority: 2,
          },
          {
            external_id: `ERP-${Date.now()}-B`,
            item_code: 'ITEM-D400',
            quantity: 80,
            due_date: '2026-06-10',
            priority: 1,
          },
        ],
      }
      const r = await api.post<{ received: number }>('/production-orders/receive', sample)
      toast.push('success', t('po.received', { n: r.received }))
      reload()
    } catch (e: any) {
      toast.push('error', e?.message ?? t('toast.actionFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <main id="main" className="container stack" style={{ paddingBlock: '2rem' }}>
      <header className="row space-between reveal">
        <div>
          <h1 className="page-title" style={{ marginBottom: '0.2rem' }}>
            <FontAwesomeIcon icon="clipboard-list" /> {t('po.title')}
          </h1>
          <p className="muted">{t('po.intro')}</p>
        </div>
        <Button
          variant="accent"
          onClick={receive}
          disabled={busy || !canWrite}
          aria-busy={busy}
          title={!canWrite ? t('po.noWritePerm') : t('po.receive')}
        >
          <FontAwesomeIcon icon={busy ? 'circle-notch' : 'plus'} spin={busy} />
          <span>{t('po.receive')}</span>
        </Button>
      </header>

      <Card glass tilt={false} spotlight={false} className="reveal">
        {!list ? (
          <SkeletonRows rows={6} />
        ) : list.length === 0 ? (
          <div className="empty-state" role="status">
            <FontAwesomeIcon icon="clipboard-list" className="icon-lg" />
            <p className="muted" style={{ margin: 0 }}>{t('po.empty')}</p>
            <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
              {t('po.emptyHint')}
            </p>
          </div>
        ) : (
          <div className="table-wrap" style={{ '--table-min-w': '640px' } as React.CSSProperties}>
            <table className="table" aria-label={t('po.aria.table')}>
              <thead>
                <tr>
                  <th scope="col">{t('po.col.id')}</th>
                  <th scope="col">{t('po.col.item')}</th>
                  <th scope="col">{t('po.col.quantity')}</th>
                  <th scope="col">{t('po.col.due')}</th>
                  <th scope="col">{t('po.col.priority')}</th>
                  <th scope="col">{t('po.col.status')}</th>
                </tr>
              </thead>
              <tbody>
                {list.map((p) => (
                  <tr key={p.production_order_id}>
                    <td className="mono">{p.production_order_id}</td>
                    <td>{p.item_code}</td>
                    <td className="mono">{p.quantity.toLocaleString('ko-KR')}</td>
                    <td>{p.due_date}</td>
                    <td>{p.priority}</td>
                    <td>
                      <span className={`badge ${p.status === 'closed' ? 'success' : p.status === 'released' ? 'info' : ''}`}>
                        {p.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </main>
  )
}
